import React, { useMemo, useState, useEffect } from 'react';
import { Loader2, Search } from 'lucide-react';
import ProductCard from './ProductCard';
import { Button } from './ui/button';
import { useProducts } from '../context/ProductContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const INITIAL_VISIBLE = 20;
const LOAD_MORE_STEP = 20;

const MarketplaceSection = () => {
  const { products, categories: apiCategories, loading, error, sync, refreshProducts } =
    useProducts();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [selectedCategory, searchQuery, sortBy]);

  const categoryOptions = useMemo(() => {
    const names = apiCategories.length
      ? apiCategories
      : [...new Set(products.map((p) => p.category).filter(Boolean))];
    return ['All', ...names.sort()];
  }, [apiCategories, products]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q) ||
          (p.category || '').toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case 'price-low':
        filtered = [...filtered].sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered = [...filtered].sort((a, b) => b.price - a.price);
        break;
      case 'name-desc':
        filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'in-stock':
        filtered = [...filtered].sort(
          (a, b) => Number(b.inStock) - Number(a.inStock) || a.name.localeCompare(b.name)
        );
        break;
      default:
        filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return filtered;
  }, [products, selectedCategory, searchQuery, sortBy]);

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, visibleCount),
    [filteredProducts, visibleCount]
  );

  return (
    <section id="shop" className="scroll-mt-24 bg-neutral-50 py-16">
      <div className="container mx-auto px-4">
        <div className="mb-10 text-center">
          <p className="mb-2 font-semibold uppercase tracking-wide text-neutral-800">
            Cloud 9 Southampton
          </p>
          <h2 className="text-4xl font-bold text-neutral-900 md:text-5xl">Shop the Menu</h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-neutral-600">
            Browse everything in stock and add items to your cart for delivery across The Hamptons.
          </p>
          {sync?.lastSyncAt && (
            <p className="mt-2 text-sm text-neutral-500">
              Last updated {new Date(sync.lastSyncAt).toLocaleString()}
            </p>
          )}
        </div>

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-neutral-950"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full lg:w-52">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc">Name: A-Z</SelectItem>
              <SelectItem value="name-desc">Name: Z-A</SelectItem>
              <SelectItem value="in-stock">In stock first</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={refreshProducts} className="shrink-0">
            Refresh
          </Button>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {categoryOptions.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setSelectedCategory(category)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                selectedCategory === category
                  ? 'bg-neutral-950 text-white'
                  : 'bg-white text-neutral-800 shadow-sm hover:bg-neutral-100'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <p className="mb-6 text-sm text-neutral-600">
          Showing {visibleProducts.length} of {filteredProducts.length} products
          {filteredProducts.length !== products.length && ` (${products.length} total)`}
          {sync?.inStockCount != null && (
            <span> · {sync.inStockCount} in stock at Cloud 9</span>
          )}
        </p>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-neutral-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading menu...
          </div>
        ) : error && !products.length ? (
          <div className="py-16 text-center">
            <p className="mb-2 text-red-600">{error}</p>
            <Button onClick={refreshProducts}>Try again</Button>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="py-16 text-center text-lg text-neutral-500">
            No products match your search.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleProducts.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                priority={index < 4}
              />
            ))}
          </div>
        )}

        {!loading && visibleProducts.length < filteredProducts.length && (
          <div className="mt-8 text-center">
            <Button
              variant="outline"
              size="lg"
              onClick={() =>
                setVisibleCount((count) =>
                  Math.min(count + LOAD_MORE_STEP, filteredProducts.length)
                )
              }
            >
              Load more products ({filteredProducts.length - visibleProducts.length} remaining)
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MarketplaceSection;
